import { getRootDomain, normalizeDomain } from "./urls";

const RESERVED_SUBDOMAINS = new Set([
  // Keep this list in sync with backend/src/projects/reserved-project-slugs.ts.
  "admin",
  "api",
  "app",
  "assets",
  "blog",
  "cdn",
  "customer-invite",
  "dashboard",
  "dev",
  "docs",
  "ftp",
  "imap",
  "invite",
  "local",
  "localhost",
  "mail",
  "pop",
  "projects",
  "smtp",
  "staging",
  "static",
  "test",
  "www",

  "signin",
  "signup",

  "about",
  "backlink-submissions",
  "canny-alternative-for-linear",
  "contact",
  "customer-roadmap-visibility",
  "discord",
  "email-digest",
  "github",
  "jira",
  "linear",
  "linear-customer-feedback",
  "linear-feature-request-board",
  "linear-import-filters",
  "linear-public-roadmap",
  "notion",
  "pricing",
  "privacy",
  "public-feedback-portal-settings",
  "slack",
  "terms",
  "webhook",
]);

const LOCALHOST_SUFFIX = ".localhost";

const cleanHost = (host?: string | null): string =>
  (host ?? "").split(",")[0]?.split(":")[0]?.trim().toLowerCase() ?? "";

const stripWww = (host: string): string => host.replace(/^www\./, "");

const isReservedSubdomain = (value: string | null): boolean =>
  !value || RESERVED_SUBDOMAINS.has(value);

export function extractProjectSubdomain(
  host?: string | null,
  rootDomain: string = getRootDomain(),
): string | null {
  const hostname = stripWww(cleanHost(host));
  const root = stripWww(normalizeDomain(rootDomain).toLowerCase());

  if (!hostname || !root || hostname === root) {
    return null;
  }

  if (root === "localhost") {
    if (hostname.endsWith(LOCALHOST_SUFFIX)) {
      const candidate = hostname.slice(
        0,
        hostname.length - LOCALHOST_SUFFIX.length,
      );
      return isReservedSubdomain(candidate) ? null : candidate;
    }
    return null;
  }

  if (!hostname.endsWith(`.${root}`)) {
    return null;
  }

  const candidate = hostname.slice(0, hostname.length - root.length - 1);
  return isReservedSubdomain(candidate) ? null : candidate;
}

export function isProjectSubdomainHost(
  host?: string | null,
  rootDomain: string = getRootDomain(),
): boolean {
  return extractProjectSubdomain(host, rootDomain) !== null;
}

type BuildPortalPathOptions = {
  host?: string | null;
  rootDomain?: string;
};

/**
 * Check if the host is a custom domain (not a subdomain of the root domain)
 */
export function isCustomDomainHost(
  host: string,
  rootDomain: string = getRootDomain(),
): boolean {
  const hostname = cleanHost(host);
  const root = rootDomain.toLowerCase().replace(/^www\./, "");

  if (!hostname || !root || hostname === root) {
    return false;
  }

  // If it's a subdomain of root domain, it's not a custom domain
  if (hostname.endsWith(`.${root}`)) {
    return false;
  }

  // If it's localhost, it's not a custom domain
  if (hostname === "localhost" || hostname.endsWith(".localhost")) {
    return false;
  }

  // Otherwise it's a custom domain
  return true;
}

export function buildPortalPath(
  slug: string,
  pathname: string = "/",
  options?: BuildPortalPathOptions,
): string {
  const normalizedSlug = slug?.trim();
  const normalizedPath =
    pathname && pathname !== "/"
      ? pathname.startsWith("/")
        ? pathname
        : `/${pathname}`
      : "/";

  const host =
    options?.host ??
    (typeof window !== "undefined" ? window.location.host : "");
  const rootDomain = options?.rootDomain ?? getRootDomain();

  const subdomain = extractProjectSubdomain(host, rootDomain);
  const alreadyScoped =
    normalizedPath === `/${normalizedSlug}` ||
    normalizedPath.startsWith(`/${normalizedSlug}/`);

  // If on a subdomain that matches the slug, don't add slug to path
  if (subdomain && normalizedSlug && subdomain === normalizedSlug) {
    return normalizedPath;
  }

  // If on a custom domain, don't add slug to path
  if (isCustomDomainHost(host, rootDomain)) {
    return normalizedPath;
  }

  if (alreadyScoped || !normalizedSlug) {
    return normalizedPath;
  }

  return `/${normalizedSlug}${normalizedPath === "/" ? "" : normalizedPath}`;
}
